import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { VwuiAngularModule, VwuiModalService } from '@bvgo-ict/vwui-angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import { LoadingSpinnerService, ProjectTypeService, ToastService } from '../../services';
import { CreateUpdateProjectTypeModalComponent, DeleteItemModalComponent } from '../../components';
import { ProjectType } from '../../models/project-analysis';

@Component({
    selector: 'app-page-project-types',
    templateUrl: './page-project-types.component.html',
    styleUrls: ['./page-project-types.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, TranslatePipe, VwuiAngularModule]
})
export class PageProjectTypesComponent implements OnInit {
    private readonly projectTypeService = inject(ProjectTypeService);
    private readonly modalService = inject(VwuiModalService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    public readonly loadingSpinnerService = inject(LoadingSpinnerService);

    public readonly projectTypes = signal<ProjectType[]>([]);

    ngOnInit(): void {
        this.loadingSpinnerService.show();

        this.projectTypeService.list()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((projectTypes) => {
                this.loadingSpinnerService.hide();
                this.projectTypes.set(projectTypes);
            });
    }

    public openUpdateProjectTypeModal(projectType: ProjectType): void {
        this.openCreateOrUpdateModal(projectType);
    }

    public openProjectType(projectType: ProjectType): void {
        this.router.navigate(['/beheer', 'project-types', projectType.id]);
    }

    public openCreateProjectTypeModal(): void {
        this.openCreateOrUpdateModal();
    }

    public openDeleteProjectTypeModal(projectType: ProjectType): void {
        const modal = this.modalService.open(DeleteItemModalComponent, {
            data: {
                title: 'PAGE_PROJECT_TYPES.delete_item.title',
                description: 'PAGE_PROJECT_TYPES.delete_item.description',
                extraDescription: null
            }
        });

        modal.afterClosed
            .pipe(
                take(1),
                filter(Boolean),
                switchMap(() => this.projectTypeService.delete(projectType.id).pipe(
                    tap(() => this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_TYPES.type_removed')))
                ))
            )
            .subscribe();
    }

    private openCreateOrUpdateModal(projectType?: ProjectType): void {
        const modal = this.modalService.open(CreateUpdateProjectTypeModalComponent, {
            closeOnBackdropClick: false,
            data: {
                title: projectType ? 'PAGE_PROJECT_TYPES.update_item.title' : 'PAGE_PROJECT_TYPES.create_item.title',
                saveText: projectType ? 'PAGE_PROJECT_TYPES.update_item.save' : 'PAGE_PROJECT_TYPES.create_item.save',
                data: projectType
            }
        });

        modal.afterClosed.subscribe((updatedProjectType: ProjectType) => {
            if (!updatedProjectType) {
                return;
            }

            if (projectType !== undefined) {
                this.projectTypeService.update(projectType.id, updatedProjectType).subscribe(() => {
                    this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_TYPES.type_modified'));
                });
            } else if (this.isNameUnique(updatedProjectType.name)) {
                this.projectTypeService.add(updatedProjectType).subscribe(() => {
                    this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_TYPES.type_added'));
                });
            } else {
                this.toastService.openError(403, this.translateService.instant('ERRORS.project_type_already_exists'));
            }
        });
    }

    private isNameUnique(name: string): boolean {
        const uniqueName = name.toLowerCase().trim();

        return !this.projectTypes().some((type) => type.name.toLowerCase().trim() === uniqueName);
    }
}
